/**
 * Site /search:
 * 1) Match custom.display_title via theme index (/collections/all?view=display-title-index)
 * 2) Boost title/SKU/display_title hits
 * 3) Shopify type=product hits (filtered — drop fuzzy junk)
 * Keep pages/articles/PDFs from Liquid.
 */
(function () {
  if (window.__pidSiteSearchBoost) return;
  window.__pidSiteSearchBoost = true;
  const MAIN_SELECTOR = '.search-grid__main';
  const INDEX_CACHE_KEY = 'pidDisplayTitleIndexV3';
  const INDEX_CACHE_TTL_MS = 30 * 60 * 1000;

  function cleanQuery(raw) {
    return String(raw || '')
      .replace(/\*/g, ' ')
      .replace(/\bOR\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^sku\s*:\s*/i, '')
      .trim();
  }

  function getQuery() {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = cleanQuery(params.get('q'));
    if (fromUrl) return fromUrl;

    const input = document.querySelector('.predictive-search-form__input, #Search, #searchfield');
    return cleanQuery(input?.value);
  }

  function getShopDomain() {
    return (
      window.bcSfFilterConfig?.shop?.domain ||
      window.Shopify?.shop ||
      ''
    );
  }

  function metafieldValue(product, key, namespace) {
    const list = product?.metafields || [];
    const match = list.find((m) => {
      if (!m || m.key !== key) return false;
      if (namespace && m.namespace && m.namespace !== namespace) return false;
      return Boolean(m.value);
    });
    return match ? String(match.value).trim() : '';
  }

  function getSku(product) {
    return (
      metafieldValue(product, 'sku', 'custom_fields') ||
      (Array.isArray(product.skus) ? product.skus.find(Boolean) : '') ||
      product.variants?.[0]?.sku ||
      product.sku ||
      ''
    )
      .toString()
      .trim();
  }

  function getDisplayTitle(product) {
    return (
      product.display_title ||
      metafieldValue(product, 'display_title', 'custom') ||
      metafieldValue(product, 'display_title') ||
      product.title ||
      ''
    ).trim();
  }

  function getPriceLabel(product) {
    const price =
      product.price ||
      metafieldValue(product, 'price', 'custom_fields');
    if (price) {
      const cleaned = String(price).replace(/\/SF/gi, '').replace(/\$/g, '').trim();
      if (!cleaned) return '';
      if (/inquire/i.test(cleaned)) return cleaned;
      if (cleaned.startsWith('$')) return cleaned;
      return `$${cleaned}`;
    }
    if (product.price_min != null) {
      return `$${(Number(product.price_min) / 100).toFixed(2)}`;
    }
    return '';
  }

  function getImage(product) {
    return (
      product.image ||
      product.images_info?.[0]?.src ||
      (typeof product.images?.[0] === 'string' ? product.images[0] : product.images?.[0]?.src) ||
      ''
    );
  }

  function combinedFieldScore(fields, query) {
    const tokens = String(query || '')
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter((token) => token.length >= 2);
    if (tokens.length < 2) return 0;

    const normalizedFields = (fields || [])
      .map((field) => String(field || '').toLowerCase().trim())
      .filter((field) => field && !field.startsWith('badge_'));

    const matchesPhrase = (phrase) => {
      const compactPhrase = phrase.replace(/[^a-z0-9]+/g, '');
      return normalizedFields.some((field) => {
        if (field.includes(phrase)) return true;
        const compactField = field.replace(/[^a-z0-9]+/g, '');
        return compactPhrase.length >= 2 && compactField.includes(compactPhrase);
      });
    };

    const used = new Array(tokens.length).fill(false);
    let chunks = 0;

    while (used.some((flag) => !flag)) {
      let best = null;
      for (let start = 0; start < tokens.length; start += 1) {
        if (used[start]) continue;
        for (let end = tokens.length; end > start; end -= 1) {
          const length = end - start;
          if (best && length <= best.length) continue;
          if (used.slice(start, end).some(Boolean)) continue;
          const phrase = tokens.slice(start, end).join(' ');
          if (matchesPhrase(phrase)) best = { start, end, length };
        }
      }
      if (!best) return 0;
      for (let i = best.start; i < best.end; i += 1) used[i] = true;
      chunks += 1;
    }

    if (chunks <= 1) return 0;
    return 55 + Math.min(4, Math.round(tokens.length / chunks - 1));
  }

  function scoreAgainstQuery(displayTitle, title, sku, query, tags) {
    const q = String(query || '')
      .toLowerCase()
      .trim()
      .replace(/^sku\s*:\s*/i, '')
      .trim();
    const dt = String(displayTitle || '').toLowerCase();
    const t = String(title || '').toLowerCase();
    const s = String(sku || '').toLowerCase();
    let score = 0;

    if (dt === q) score = 100;
    else if (t === q || s === q) score = 95;
    else if (q && dt.startsWith(q)) score = 80;
    else if (q && (t.startsWith(q) || s.startsWith(q))) score = 75;
    else if (q && dt.includes(q)) score = 60;
    else if (q && (t.includes(q) || s.includes(q))) score = 55;

    const fields = [dt, t, s];
    const tagList = Array.isArray(tags)
      ? tags
      : String(tags || '')
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);
    tagList.forEach((tag) => {
      const value = String(tag || '').toLowerCase().trim();
      if (!value || value.startsWith('badge_')) return;
      fields.push(value);
      if (value === q) score = Math.max(score, 90);
      else if (q && value.startsWith(q)) score = Math.max(score, 70);
      else if (q && value.includes(q)) score = Math.max(score, 55);
    });

    if (q.includes(' ') && fields.some((field) => field && field.includes(q))) {
      score = Math.max(score, 64);
    }

    const combo = combinedFieldScore(fields, q);
    if (combo) score = Math.max(score, combo);

    return score;
  }

  function scoreBoostProduct(product, query) {
    const tags = [];
    const rawTags = product.tags || product.tag;
    if (Array.isArray(rawTags)) tags.push(...rawTags);
    else if (rawTags) tags.push(...String(rawTags).split(','));
    const species =
      product.species || metafieldValue(product, 'species', 'custom_fields');
    if (species) tags.push(species);

    return scoreAgainstQuery(
      getDisplayTitle(product),
      product.title,
      getSku(product),
      query,
      tags
    );
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  const COPY_ICON_SVG = `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M1.96286 16.0373C2.38255 16.4576 2.88116 16.7908 3.43003 17.0179C3.97891 17.2449 4.56723 17.3612 5.16119 17.3601C5.75528 17.3611 6.34373 17.2448 6.89275 17.0178C7.44176 16.7908 7.94054 16.4576 8.36043 16.0373L10.9191 13.4778L9.63976 12.1984L7.08109 14.758C6.57123 15.2656 5.88108 15.5505 5.16164 15.5505C4.4422 15.5505 3.75205 15.2656 3.24219 14.758C2.73417 14.2484 2.4489 13.5581 2.4489 12.8385C2.4489 12.119 2.73417 11.4287 3.24219 10.9191L5.80176 8.36043L4.52243 7.08109L1.96286 9.63976C1.11582 10.4888 0.640137 11.6392 0.640137 12.8385C0.640137 14.0379 1.11582 15.1883 1.96286 16.0373V16.0373ZM16.0373 8.36043C16.8839 7.51111 17.3593 6.36083 17.3593 5.16164C17.3593 3.96245 16.8839 2.81217 16.0373 1.96286C15.1883 1.11582 14.0379 0.640137 12.8385 0.640137C11.6392 0.640137 10.4888 1.11582 9.63976 1.96286L7.08109 4.52243L8.36043 5.80176L10.9191 3.24219C11.429 2.73461 12.1191 2.44964 12.8385 2.44964C13.558 2.44964 14.2481 2.73461 14.758 3.24219C15.266 3.75182 15.5513 4.44205 15.5513 5.16164C15.5513 5.88123 15.266 6.57147 14.758 7.08109L12.1984 9.63976L13.4778 10.9191L16.0373 8.36043Z" fill="black"/>
    <path d="M5.80075 13.4786L4.52051 12.1993L12.1992 4.52148L13.4786 5.80172L5.80075 13.4786Z" fill="black"/>
  </svg>`;

  function formatCopyLabel(sku, title) {
    const safeSku = String(sku || '').trim();
    const safeTitle = String(title || '').trim();
    if (safeSku && safeTitle) return `SKU: ${safeSku}, ${safeTitle}`;
    if (safeTitle) return safeTitle;
    if (safeSku) return `SKU: ${safeSku}`;
    return '';
  }

  function absoluteProductUrl(urlOrPath) {
    if (!urlOrPath) return '';
    try {
      return new URL(urlOrPath, window.location.origin).href;
    } catch (e) {
      return urlOrPath;
    }
  }

  function getBadge(product) {
    if (product?.badge_name) {
      return { name: product.badge_name, icon: product.badge_icon || '' };
    }

    const tags = product?.tags || product?.tag || [];
    const list = Array.isArray(tags) ? tags : [];
    const tag = list.find((item) => String(item).includes('badge_'));
    if (!tag) return null;

    const name = String(tag)
      .split('_')
      .slice(1)
      .join(' ')
      .replace(/-/g, ' ')
      .toUpperCase();
    const fileBase = window.bcSfFilterConfig?.general?.file_url?.split('?')[0] || '';
    return {
      name,
      icon: fileBase ? `${fileBase}${tag}.png` : '',
    };
  }

  function renderBadgeHtml(product) {
    const badge = getBadge(product);
    if (!badge?.name) return '';
    const icon = badge.icon
      ? `<div class="grid__item-badge-image"><img src="${escapeHtml(badge.icon)}" alt="${escapeHtml(badge.name)}" width="24" height="24"></div>`
      : '';
    return `<div class="grid__item-badge">${icon}<span class="grid__item-badge-name">${escapeHtml(badge.name)}</span></div>`;
  }

  function renderProductCard(product) {
    const displayTitle = getDisplayTitle(product);
    const sku = getSku(product);
    const price = getPriceLabel(product);
    const species =
      product.species ||
      metafieldValue(product, 'species', 'custom_fields');
    const url = product.url || `/products/${product.handle}`;
    const href = absoluteProductUrl(url);
    const image = getImage(product);
    const variantId =
      product.variant_id ||
      product.variants?.[0]?.id ||
      '';
    const copyLabel = formatCopyLabel(sku, displayTitle);

    const imageBlock = image
      ? `<div class="grid__item one-sixth" style="position: relative;">
          ${renderBadgeHtml(product)}
          <a href="${escapeHtml(href)}" title="${escapeHtml(displayTitle)}">
            <img src="${escapeHtml(image)}" alt="${escapeHtml(displayTitle)}" loading="lazy">
          </a>
        </div>
        <div class="grid__item five-sixths">`
      : `<div class="grid__item five-sixths push--one-sixth">`;

    return `<div class="grid product" data-boost-search-item data-handle="${escapeHtml(product.handle)}">
      ${imageBlock}
        <a href="${escapeHtml(href)}" class="product__copy-text" style="color: rgb(64, 64, 177); font-weight: 400; font-size: 12px; text-decoration: underline; text-decoration-color: black; position: absolute; transform: translateX(90000px);">${escapeHtml(copyLabel)}</a>
        <p class="h3--body search__product">
          <a href="${escapeHtml(href)}">${escapeHtml(displayTitle)}</a>
          <button class="product__copy-btn" aria-label="Copy product link" type="button">
            ${COPY_ICON_SVG}
            <span class="product__text-on-hover">Copy product link</span>
          </button>
        </p>
        ${sku ? `<p style="margin: 0px">SKU: ${escapeHtml(sku)}</p>` : ''}
        ${species ? `<p style="margin: 0px">Species: ${escapeHtml(species)}</p>` : ''}
        ${price ? `<p style="margin: 0px">Price Per SF: ${escapeHtml(price)}</p>` : ''}
        <div
          class="wk-wishlist-button wk-wishlist-button--checkbox"
          data-product-handle="${escapeHtml(product.handle)}"
          data-variant-id="${escapeHtml(variantId)}"
        >
          <div class="wk-wishlist-button__inner" style="gap:7px">
            <span>Add to Favorites</span>
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <path d="M32.012,59.616c-1.119-.521-2.365-1.141-3.707-1.859a79.264,79.264,0,0,1-11.694-7.614C6.316,42,.266,32.6.254,22.076,0.244,12.358,7.871,4.506,17.232,4.5a16.661,16.661,0,0,1,11.891,4.99l2.837,2.889,2.827-2.9a16.639,16.639,0,0,1,11.874-5.02h0c9.368-.01,17.008,7.815,17.021,17.539,0.015,10.533-6.022,19.96-16.312,28.128a79.314,79.314,0,0,1-11.661,7.63C34.369,58.472,33.127,59.094,32.012,59.616Z"></path>
            </svg>
          </div>
        </div>
      </div>
    </div>`;
  }

  function getSkuFromCard(card) {
    const paragraphs = Array.from(card.querySelectorAll('p'));
    for (const p of paragraphs) {
      if (p.classList.contains('search__product') || p.classList.contains('h3--body')) continue;
      const text = (p.textContent || '').trim();
      const match = text.match(/^SKU:\s*(.+)$/i);
      if (match) return match[1].trim();
    }
    return '';
  }

  function getTitleFromCard(card) {
    const titleEl = card.querySelector('.search__product > a:not(.product__copy-text), .h3--body.search__product > a:not(.product__copy-text)');
    return (titleEl?.textContent || '').trim();
  }

  function prepareSearchCardCopy(card) {
    if (!card) return;

    const title = getTitleFromCard(card);
    const sku = getSkuFromCard(card);
    const titleEl = card.querySelector('.search__product > a:not(.product__copy-text), .h3--body.search__product > a:not(.product__copy-text)');
    const href = absoluteProductUrl(
      card.querySelector('a.product__copy-text')?.getAttribute('href') ||
        titleEl?.getAttribute('href') ||
        ''
    );
    const label = formatCopyLabel(sku, title);
    if (!label) return;

    let copyLink = card.querySelector('a.product__copy-text');
    if (!copyLink && titleEl) {
      copyLink = document.createElement('a');
      copyLink.className = 'product__copy-text';
      copyLink.style.cssText =
        'color: rgb(64, 64, 177); font-weight: 400; font-size: 12px; text-decoration: underline; text-decoration-color: black; position: absolute; transform: translateX(90000px);';
      titleEl.parentElement?.insertBefore(copyLink, titleEl);
    }

    if (copyLink) {
      if (href) copyLink.setAttribute('href', href);
      copyLink.textContent = label;
    }

    const titleWrap = card.querySelector('.search__product, .h3--body.search__product');
    let btn = card.querySelector('.product__copy-btn');
    if (!btn && titleWrap) {
      btn = document.createElement('button');
      btn.className = 'product__copy-btn';
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Copy product link');
      btn.innerHTML = `${COPY_ICON_SVG}<span class="product__text-on-hover">Copy product link</span>`;
      titleWrap.appendChild(btn);
    } else if (btn && !btn.querySelector('svg')) {
      btn.insertAdjacentHTML('afterbegin', COPY_ICON_SVG);
    }
  }

  function showCopyPopup() {
    const popup = document.querySelector('.product-link-copy-popup');
    if (!popup) return;
    popup.classList.add('product-link-copy-popup_show');
    setTimeout(() => popup.classList.remove('product-link-copy-popup_show'), 2000);
  }

  async function copySearchProduct(button) {
    const card = button.closest('.grid.product') || button.closest('.grid');
    if (!card) return;

    prepareSearchCardCopy(card);

    const copyLink = card.querySelector('.product__copy-text');
    const text = (copyLink?.textContent || '').trim();
    const href = absoluteProductUrl(copyLink?.getAttribute('href') || '');
    if (!text || !href) return;

    const safeText = escapeHtml(text);
    const html = `<a href="${escapeHtml(href)}">${safeText}</a>`;

    try {
      if (navigator.clipboard?.write && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': new Blob([text], { type: 'text/plain' }),
            'text/html': new Blob([html], { type: 'text/html' }),
          }),
        ]);
        showCopyPopup();
        return;
      }
    } catch (error) {
      console.warn('[site-search-boost] rich copy failed', error);
    }

    try {
      await navigator.clipboard.writeText(`${text}\n${href}`);
      showCopyPopup();
    } catch (error) {
      console.warn('[site-search-boost] plain copy failed', error);
    }
  }

  function bindSearchCopyDelegation() {
    if (document.body.dataset.searchCopyBound === 'true') return;
    document.body.dataset.searchCopyBound = 'true';

    document.addEventListener('click', (event) => {
      const button = event.target.closest('.new-search-result .product__copy-btn, .search-grid__main .product__copy-btn');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      copySearchProduct(button);
    });
  }

  async function fetchBoostProducts(query) {
    const shop = getShopDomain();
    if (!shop || !query) return [];

    const url = `https://services.mybcapps.com/bc-sf-filter/search?shop=${encodeURIComponent(shop)}&q=${encodeURIComponent(query)}&limit=250`;
    const response = await fetch(url);
    if (!response.ok) return [];

    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  async function fetchShopifyProductCards(query) {
    if (!query) return [];

    const url = `/search?q=${encodeURIComponent(query)}&type=product&options[prefix]=last`;
    const response = await fetch(url);
    if (!response.ok) return [];

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const main = doc.querySelector(MAIN_SELECTOR);
    if (!main) return [];

    return Array.from(main.children).filter(
      (el) => el.classList.contains('product') || el.querySelector?.('.search__product')
    );
  }

  function cardMatchesQuery(el, query) {
    const q = query.toLowerCase();
    const title = (
      el.querySelector('.search__product a, .h3--body a, .product-title')?.textContent || ''
    )
      .trim()
      .toLowerCase();
    const sku = (el.textContent.match(/SKU:\s*([^\n]+)/i)?.[1] || '').trim().toLowerCase();
    return scoreAgainstQuery(title, title, sku, q) >= 55;
  }

  function getHandleFromEl(el) {
    if (!el) return '';
    if (el.getAttribute?.('data-handle')) return el.getAttribute('data-handle');
    const href = el.querySelector?.('a[href*="/products/"]')?.getAttribute('href') || '';
    return href.match(/\/products\/([^/?#]+)/)?.[1] || '';
  }

  function partitionShopifyItems(main) {
    const products = [];
    const others = [];

    Array.from(main.children).forEach((el) => {
      if (el.classList.contains('product') || el.querySelector?.('.search__product')) {
        products.push(el);
      } else {
        others.push(el);
      }
    });

    return { products, others };
  }

  function readIndexCache() {
    try {
      const raw = sessionStorage.getItem(INDEX_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.at || !Array.isArray(parsed.items)) return null;
      if (Date.now() - parsed.at > INDEX_CACHE_TTL_MS) return null;
      return parsed.items;
    } catch (e) {
      return null;
    }
  }

  function writeIndexCache(items) {
    try {
      sessionStorage.setItem(
        INDEX_CACHE_KEY,
        JSON.stringify({ at: Date.now(), items })
      );
    } catch (e) {
      // ignore quota errors
    }
  }

  async function fetchDisplayTitleIndexPage(page) {
    const url = `/collections/all?view=display-title-index&page=${page}`;
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) return { items: [], nextPage: null };

    const text = await response.text();
    const jsonText = text.replace(/<!--NEXT_PAGE:\d+-->/g, '').trim();
    let items = [];
    try {
      items = JSON.parse(jsonText);
    } catch (e) {
      console.warn('[site-search-boost] bad display-title index page', page, e);
      items = [];
    }

    const nextMatch = text.match(/<!--NEXT_PAGE:(\d+)-->/);
    return {
      items: Array.isArray(items) ? items : [],
      nextPage: nextMatch ? Number(nextMatch[1]) : null,
    };
  }

  async function loadDisplayTitleIndex() {
    const cached = readIndexCache();
    if (cached?.length) return cached;

    const all = [];
    let page = 1;
    let guard = 0;

    while (page && guard < 100) {
      guard += 1;
      const { items, nextPage } = await fetchDisplayTitleIndexPage(page);
      all.push(...items);
      page = nextPage;
      if (!items.length) break;
    }

    writeIndexCache(all);
    return all;
  }

  function matchDisplayTitleIndex(index, query) {
    return index
      .map((item) => ({
        item,
        score: scoreAgainstQuery(
          item.display_title,
          item.display_title,
          item.sku,
          query,
          []
            .concat(item.tags || [], item.species || [])
            .filter(Boolean)
        ),
      }))
      .filter((row) => row.score >= 55)
      .sort((a, b) => b.score - a.score)
      .map((row) => row.item);
  }

  const RESULTS_PAGE_SIZE = 16;
  let searchResultState = null;

  function getResultsPage() {
    const page = Number(new URLSearchParams(window.location.search).get('page') || '1');
    return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  }

  function setResultsPage(page) {
    const url = new URL(window.location.href);
    if (page <= 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(page));
    history.pushState({ searchPage: page }, '', url);
  }

  function paginationWindow(current, total) {
    const pages = new Set([1, total, current - 1, current, current + 1]);
    return [...pages].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  }

  function renderPaginationHtml(current, total) {
    if (total <= 1) return '';

    const prev =
      current > 1
        ? `<li><a href="#" data-search-page="${current - 1}">&larr;</a></li>`
        : `<li class="disabled"><span>&larr;</span></li>`;
    const next =
      current < total
        ? `<li><a href="#" data-search-page="${current + 1}">&rarr;</a></li>`
        : `<li class="disabled"><span>&rarr;</span></li>`;

    let items = '';
    let previous = 0;
    paginationWindow(current, total).forEach((page) => {
      if (previous && page - previous > 1) {
        items += `<li><span>&hellip;</span></li>`;
      }
      items +=
        page === current
          ? `<li class="active"><span>${page}</span></li>`
          : `<li><a href="#" data-search-page="${page}">${page}</a></li>`;
      previous = page;
    });

    return `<ul class="pagination-custom">${prev}${items}${next}</ul>`;
  }

  function ensurePaginationMount(main) {
    const host = main.closest('.new-search-result') || main.parentElement;
    if (!host) return null;
    let mount = host.querySelector(':scope > .search-results-pagination');
    if (!mount) {
      mount = document.createElement('div');
      mount.className = 'text-center search-results-pagination';
      host.appendChild(mount);
    }
    return mount;
  }

  function paintSearchResults() {
    if (!searchResultState) return;
    const { main, productHtmlParts, others } = searchResultState;
    const total = productHtmlParts.length;
    const totalPages = Math.max(1, Math.ceil(total / RESULTS_PAGE_SIZE));
    const page = Math.min(getResultsPage(), totalPages);
    const start = (page - 1) * RESULTS_PAGE_SIZE;
    const pageProducts = productHtmlParts.slice(start, start + RESULTS_PAGE_SIZE);
    const othersHtml = page === 1 ? others.map((el) => el.outerHTML).join('') : '';

    if (!pageProducts.length && !othersHtml) {
      main.innerHTML = '';
      const mount = ensurePaginationMount(main);
      if (mount) mount.innerHTML = '';
      syncSearchEmptyHint();
      return;
    }

    main.innerHTML = pageProducts.join('') + othersHtml;

    main.querySelectorAll('.grid.product').forEach((card) => {
      prepareSearchCardCopy(card);
    });
    main.querySelectorAll('.search__product').forEach((el) => {
      const card = el.closest('.grid');
      if (card) prepareSearchCardCopy(card);
    });

    const mount = ensurePaginationMount(main);
    if (mount) mount.innerHTML = renderPaginationHtml(page, totalPages);

    document.querySelectorAll('.new-pagination').forEach((el) => {
      el.style.display = 'none';
    });

    syncSearchEmptyHint();
  }

  function bindSearchPagination() {
    if (document.body.dataset.searchPaginationBound === 'true') return;
    document.body.dataset.searchPaginationBound = 'true';

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-search-page]');
      if (!link) return;
      event.preventDefault();
      const page = Number(link.getAttribute('data-search-page'));
      if (!Number.isFinite(page) || page < 1) return;
      setResultsPage(page);
      paintSearchResults();
      document.querySelector('.new-search-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    window.addEventListener('popstate', () => {
      paintSearchResults();
    });
  }

  function applyResults(main, displayTitleMatches, boostProducts, shopifyProductCards, query) {
    const allowed = getAllowedSearchTypes();
    const others = filterOthersByType(partitionShopifyItems(main).others);

    const rankedBoost = boostProducts
      .map((product) => ({ product, score: scoreBoostProduct(product, query) }))
      .filter((row) => row.score >= 55)
      .sort((a, b) => b.score - a.score);

    const seen = new Set();
    const productHtmlParts = [];

    if (allowed.product) {
      displayTitleMatches.forEach((item) => {
        if (!item?.handle || seen.has(item.handle)) return;
        seen.add(item.handle);
        productHtmlParts.push(renderProductCard(item));
      });

      rankedBoost.forEach((row) => {
        const handle = row.product.handle;
        if (!handle || seen.has(handle)) return;
        seen.add(handle);
        productHtmlParts.push(renderProductCard(row.product));
      });

      shopifyProductCards.forEach((el) => {
        if (!cardMatchesQuery(el, query)) return;
        const handle = getHandleFromEl(el);
        if (!handle || seen.has(handle)) return;
        seen.add(handle);
        productHtmlParts.push(el.outerHTML);
      });
    }

    searchResultState = { main, productHtmlParts, others, query };
    paintSearchResults();
  }

  function getSearchType() {
    return (new URLSearchParams(window.location.search).get('type') || '').toLowerCase().trim();
  }

  function getAllowedSearchTypes() {
    const type = getSearchType();
    if (!type) return { product: true, page: true, article: true, pdf: true };
    const parts = type.split(',').map((part) => part.trim()).filter(Boolean);
    return {
      product: parts.includes('product'),
      page: parts.includes('page'),
      article: parts.includes('article'),
      pdf: parts.includes('pdf') && parts.length === 1,
    };
  }

  function isPdfSearch() {
    const allowed = getAllowedSearchTypes();
    return allowed.pdf && !allowed.product && !allowed.page && !allowed.article;
  }

  function isProductScopedSearch() {
    return getAllowedSearchTypes().product;
  }

  function filterOthersByType(others) {
    const allowed = getAllowedSearchTypes();
    return others.filter((el) => {
      if (el.classList.contains('page') || el.querySelector?.('a[href*="/pages/"]')) {
        return allowed.page;
      }
      if (el.classList.contains('article') || el.querySelector?.('a[href*="/blogs/"]')) {
        return allowed.article;
      }
      if (el.classList.contains('product') || el.querySelector?.('.search__product')) {
        return allowed.product;
      }
      return allowed.page || allowed.article;
    });
  }

  function hasSearchResults(root = document) {
    return Boolean(
      root.querySelector(
        '.search-grid__main .grid.product, .search-grid__main .grid.page, .search-grid__main .grid.article, .search-grid__main .search__product, .pdf-result-item'
      )
    );
  }

  function syncSearchEmptyHint() {
    const hint = document.querySelector('[data-search-empty-hint]');
    const related = document.querySelector('.new-search-result');
    if (isPdfSearch()) return;

    const main = document.querySelector(MAIN_SELECTOR);
    if (main?.getAttribute('data-boost-search-loading') === 'true') {
      if (hint) hint.style.display = 'none';
      return;
    }

    const hasResults = hasSearchResults();
    if (hint) hint.style.display = hasResults ? 'none' : '';
    if (related) related.style.display = hasResults ? '' : 'none';
  }

  async function init() {
    bindSearchCopyDelegation();
    bindSearchPagination();

    if (!document.body.classList.contains('template-search') && !/\/search/.test(window.location.pathname)) {
      return;
    }

    const query = getQuery();
    if (!query) return;

    if (isPdfSearch()) return;

    const main = document.querySelector(MAIN_SELECTOR);
    if (!main) {
      syncSearchEmptyHint();
      return;
    }

    if (!isProductScopedSearch()) {
      const allowed = getAllowedSearchTypes();
      Array.from(main.children).forEach((el) => {
        const isProduct = el.classList.contains('product') || el.querySelector?.('.search__product');
        const isPage = el.classList.contains('page');
        const isArticle = el.classList.contains('article');
        if (isProduct || (isPage && !allowed.page) || (isArticle && !allowed.article)) {
          el.remove();
        }
      });
      main.removeAttribute('data-boost-search-loading');
      syncSearchEmptyHint();
      return;
    }

    main.setAttribute('data-boost-search-loading', 'true');
    syncSearchEmptyHint();

    try {
      const [index, boostProducts, shopifyProductCards] = await Promise.all([
        loadDisplayTitleIndex(),
        fetchBoostProducts(query),
        fetchShopifyProductCards(query),
      ]);

      const displayTitleMatches = matchDisplayTitleIndex(index, query);
      applyResults(main, displayTitleMatches, boostProducts, shopifyProductCards, query);
    } catch (error) {
      console.warn('[site-search-boost]', error);
    } finally {
      main.removeAttribute('data-boost-search-loading');
      syncSearchEmptyHint();
    }
  }

  function prefetchDisplayTitleIndex() {
    loadDisplayTitleIndex().catch(() => {});
  }

  function bindPrefetch() {
    const field = document.querySelector('#searchfield, .predictive-search-form__input, #Search');
    field?.addEventListener('focus', prefetchDisplayTitleIndex, { once: true });
    document.querySelector('#searchform')?.addEventListener('submit', prefetchDisplayTitleIndex);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bindPrefetch();
      init();
    });
  } else {
    bindPrefetch();
    init();
  }
})();
